// Updated DataStores listing page with real API integration
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DatabaseZap } from 'lucide-react';
import AddVectorStoreModal from './VectorStore/AddVectorStoreModal';
import { ingestionService } from '../../../components/api/ingestionService';
import PermissionsAssignmentModal from '../common/Permissions/PermissionsAssignmentModal';
import ConfirmDialog from '../Dialogs/ConfirmDialog';
import VaultEntityCard from './DataVault/VaultEntityCard';

const DataStores = ({ isLight = false, requestAdd = 0 }: { isLight?: boolean; requestAdd?: number }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [datastoresList, setDatastoresList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<any | null>(null);
  const [showAssignmentModal, setAssignmentModal] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ingestionService.getVectorStores();
      setDatastoresList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load vector datastores.');
      setDatastoresList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastAddTick = useRef(requestAdd);
  useEffect(() => {
    if (requestAdd === lastAddTick.current) return;
    lastAddTick.current = requestAdd;
    if (requestAdd < 1) return;
    setEditingItem(null);
    setShowAddModal(true);
  }, [requestAdd]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError(null);
    try {
      await ingestionService.deleteVectorStore(id);
      await fetchList();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete.');
    } finally {
      setDeletingId(null);
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const openEdit = (ds: any) => {
    setEditingItem(ds);
    setShowAddModal(true);
  };

  return (
    <div>
      {error ? <p className="mb-3 text-sm text-red-400">{error}</p> : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading ? (
          <div className={`col-span-full text-center py-10 text-sm ${muted}`}>Loading…</div>
        ) : (
          datastoresList.map((ds) => {
            const title = ds.description || ds.collectionName || 'Untitled datastore';
            const details = [ds.provider, ds.collectionName, ds.endpointUrl].filter(Boolean).join(' · ');
            return (
              <VaultEntityCard
                key={ds.id}
                isLight={isLight}
                title={title}
                description={details || 'No description provided.'}
                tag={ds.isActive ? 'Active' : 'Inactive'}
                icon={DatabaseZap}
                iconClass={isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'}
                inactive={!ds.isActive}
                onOpen={() => openEdit(ds)}
                onEdit={() => openEdit(ds)}
                onPermissions={() => setAssignmentModal(true)}
                onDelete={() => setItemToDelete(ds)}
                deleteDisabled={deletingId === ds.id}
              />
            );
          })
        )}
      </div>

      {!loading && datastoresList.length === 0 ? (
        <div className={`text-center py-10 text-sm ${muted}`}>No datastores found.</div>
      ) : null}

      {showAddModal && (
        <AddVectorStoreModal
          initialData={editingItem}
          onClose={() => { setShowAddModal(false); setEditingItem(null); }}
          onSubmit={async (data) => {
            setError(null);
            const providerMap: Record<string, string> = {
              Pinecone: 'Pinecone',
              Qdrant: 'Qdrant',
              ChromaDB: 'Chroma',
              Weaviate: 'Custom',
              Milvus: 'Milvus',
              FAISS: 'Custom',
            };
            const dto = {
              provider: providerMap[data.type] || data.type || 'Custom',
              apiKey: data.apiKey || undefined,
              endpointUrl: data.endpoint || undefined,
              collectionName: data.name?.trim() || 'Unnamed',
              isDefault: false,
              isActive: data.status === 'Active',
              description: data.description || undefined,
              region: data.region || undefined,
              intendedUsage: data.intendedUsage || undefined,
              tenantOrClient: data.tenantClient || undefined,
            };
            if (editingItem?.id) {
              await ingestionService.updateVectorStore(editingItem.id, dto);
            } else {
              await ingestionService.createVectorStore(dto);
            }
            setShowAddModal(false);
            setEditingItem(null);
            await fetchList();
          }}
        />
      )}

      {showAssignmentModal && (
        <PermissionsAssignmentModal
          onClose={() => setAssignmentModal(false)}
          onSave={() => { }}
          entityType="vector-store"
          entityName="Vector Store"
        />
      )}

      {itemToDelete && (
        <ConfirmDialog
          title="Delete Datastore"
          message={`Delete "${itemToDelete.description || itemToDelete.collectionName || 'this datastore'}"? This cannot be undone.`}
          onYes={() => {
            const id = itemToDelete.id;
            setItemToDelete(null);
            handleDelete(id);
          }}
          onCancel={() => setItemToDelete(null)}
        />
      )}
    </div>
  );
};

export default DataStores;
